import React, { useCallback, useRef, useEffect, useState, forwardRef, useImperativeHandle, useMemo } from 'react'
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  ReactFlowProvider,
  Handle,
  Position,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { toPng } from 'html-to-image'
import TextEditModal from './TextEditModal'
import { useTabs } from '../../contexts/TabsContext'

// Memoization cache for parsed note links
const parseNoteLinksCache = new Map()

// Helper function to parse note links in text
const parseNoteLinks = (text, onNoteClick) => {
  if (!text) return text
  
  // Check cache first
  const cacheKey = `${text}-${onNoteClick.toString()}`
  if (parseNoteLinksCache.has(cacheKey)) {
    return parseNoteLinksCache.get(cacheKey)
  }
  
  // Regular expression to match note links: [text](note:noteId)
  const noteLinkRegex = /\[([^\]]+)\]\(note:([^)]+)\)/g
  
  const parts = []
  let lastIndex = 0
  let match
  
  while ((match = noteLinkRegex.exec(text)) !== null) {
    // Safety check for match groups
    if (!match[1] || !match[2]) {
      console.warn('Invalid note link format:', match[0])
      continue
    }
    
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }
    
    // Add the clickable link
    const linkText = match[1]
    const noteId = match[2]
    
    parts.push(
      <span
        key={`link-${match.index}`}
        className="text-blue-600 dark:text-blue-400 cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 border-2 border-blue-500 rounded-md px-1 py-0.5"
        onClick={(e) => {
          e.stopPropagation()
          onNoteClick(noteId)
        }}
        title={`Open note: ${linkText}`}
      >
        {linkText}
      </span>
    )
    
    lastIndex = match.index + match[0].length
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }
  
  const result = parts.length > 1 ? parts : text
  
  // Cache the result (limit cache size to prevent memory leaks)
  if (parseNoteLinksCache.size > 100) {
    const firstKey = parseNoteLinksCache.keys().next().value
    parseNoteLinksCache.delete(firstKey)
  }
  parseNoteLinksCache.set(cacheKey, result)
  
  return result
}

// Function to create node types with note click handler
const createNodeTypes = (onNoteClick) => ({
  input: ({ data, isConnectable }) => (
    <div className="px-4 py-2 shadow-md rounded-md bg-green-100 border-2 border-green-500 text-green-800 relative select-none">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="react-flow-handle react-flow-handle-green"
      />
      <div className="font-bold">{parseNoteLinks(data.label, onNoteClick)}</div>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="react-flow-handle react-flow-handle-green"
      />
    </div>
  ),
  default: ({ data, isConnectable }) => (
    <div className="px-4 py-2 shadow-md rounded-md bg-blue-100 border-2 border-blue-500 text-blue-800 relative select-none">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="react-flow-handle react-flow-handle-blue"
      />
      <div className="font-bold">{parseNoteLinks(data.label, onNoteClick)}</div>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="react-flow-handle react-flow-handle-blue"
      />
    </div>
  ),
  output: ({ data, isConnectable }) => (
    <div className="px-4 py-2 shadow-md rounded-md bg-red-100 border-2 border-red-500 text-red-800 relative select-none">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="react-flow-handle react-flow-handle-red"
      />
      <div className="font-bold">{parseNoteLinks(data.label, onNoteClick)}</div>
    </div>
  ),
  decision: ({ data, isConnectable }) => {
    // Calculate dynamic sizing based on text content
    const text = data.label || ''
    const lines = text.split('\n')
    const maxLineLength = Math.max(...lines.map(line => line.length))
    
    // Dynamic width: minimum 80px, grow based on text length, max 200px
    const minSize = 80
    const maxSize = 200
    const charWidth = 8 // Approximate character width in pixels
    const padding = 24 // Account for rotation and padding
    const dynamicWidth = Math.max(minSize, Math.min(maxSize, maxLineLength * charWidth + padding))
    
    // Dynamic height: minimum 80px, grow based on number of lines, max 200px
    const lineHeight = 16 // Approximate line height for rotated text
    const verticalPadding = 16
    const dynamicHeight = Math.max(minSize, Math.min(maxSize, lines.length * lineHeight + verticalPadding))
    
    // Use the larger dimension to maintain 1:1 aspect ratio (square diamond), capped at 200px
    const finalSize = Math.min(maxSize, Math.max(dynamicWidth, dynamicHeight))
    
    return (
      <div 
        className="shadow-md rounded-lg bg-yellow-100 border-2 border-yellow-500 text-yellow-800 transform rotate-45 relative select-none flex items-center justify-center"
        style={{
          width: `${finalSize}px`,
          height: `${finalSize}px`,
          minWidth: `${minSize}px`,
          minHeight: `${minSize}px`
        }}
      >
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          className="react-flow-handle react-flow-handle-yellow react-flow-handle-decision-top"
        />
        <div 
          style={{
            width: `${finalSize - 40}px`,
            height: `${finalSize - 40}px`,
            display: 'table'
          }}
        >
          <div 
            className="font-bold transform -rotate-45 text-center text-sm"
            style={{
              width: '100%',
              height: '100%',
              lineHeight: '1.2',
              textAlign: 'center',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              hyphens: 'auto',
              display: 'table-cell',
              verticalAlign: 'middle',
              padding: '4px'
            }}
          >
            {parseNoteLinks(text, onNoteClick)}
          </div>
        </div>
        <Handle
          type="source"
          position={Position.Bottom}
          id="yes"
          isConnectable={isConnectable}
          className="react-flow-handle react-flow-handle-yellow react-flow-handle-decision-bottom"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="no"
          isConnectable={isConnectable}
          className="react-flow-handle react-flow-handle-yellow react-flow-handle-decision-right"
        />
      </div>
    )
  },
  process: ({ data, isConnectable }) => {
    // Calculate dynamic sizing based on text content
    const text = data.label || ''
    const lines = text.split('\n')
    const maxLineLength = Math.max(...lines.map(line => line.length))
    
    // Dynamic width: minimum 120px, grow based on text length
    const minWidth = 120
    const charWidth = 8 // Approximate character width in pixels
    const padding = 32 // px-4 (16px) * 2
    const dynamicWidth = Math.max(minWidth, maxLineLength * charWidth + padding)
    
    // Dynamic height: minimum 40px, grow based on number of lines
    const minHeight = 40
    const lineHeight = 20 // Approximate line height
    const verticalPadding = 16 // py-2 (8px) * 2
    const dynamicHeight = Math.max(minHeight, lines.length * lineHeight + verticalPadding)
    
    return (
      <div 
        className="shadow-md rounded-md bg-blue-100 border-2 border-blue-500 text-blue-800 relative select-none"
        style={{
          minWidth: `${dynamicWidth}px`,
          minHeight: `${dynamicHeight}px`,
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          className="react-flow-handle react-flow-handle-blue"
        />
        <div 
          className="font-bold text-center whitespace-pre-wrap break-words"
          style={{
            maxWidth: `${dynamicWidth - 32}px`, // Account for padding
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {parseNoteLinks(text, onNoteClick)}
        </div>
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={isConnectable}
          className="react-flow-handle react-flow-handle-blue"
        />
      </div>
    )
  },
  database: ({ data, isConnectable }) => (
    <div className="px-4 py-2 shadow-md rounded-md bg-gray-100 border-2 border-gray-500 text-gray-800 rounded-full relative select-none">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="react-flow-handle react-flow-handle-gray"
      />
      <div className="font-bold">{parseNoteLinks(data.label, onNoteClick)}</div>
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="react-flow-handle react-flow-handle-gray"
      />
    </div>
  ),
})

const FlowchartCanvas = forwardRef(({ 
  initialNodes = [], 
  initialEdges = [], 
  onNodesChange: onNodesChangeProp,
  onEdgesChange: onEdgesChangeProp,
  onConnect: onConnectProp,
  onFlowchartChange,
  onSelectionChange,
  initialData,
  noteId = null,
  readOnly = false,
  className = '',
  style = {},
  theme = 'light'
}, ref) => {
  const reactFlowWrapper = useRef(null)
  const { openTab } = useTabs()
  
  // Initialize with data from initialData if available, otherwise use initialNodes/initialEdges
  const [nodes, setNodes, onNodesChange] = useNodesState(initialData?.jsonData?.nodes || initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialData?.jsonData?.edges || initialEdges)
  const [reactFlowInstance, setReactFlowInstance] = useState(null)
  const [currentEdgeType, setCurrentEdgeType] = useState('default')
  
  // Text edit modal state
  const [isTextModalOpen, setIsTextModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null) // 'node' or 'edge'
  const [editingId, setEditingId] = useState(null)
  const [editingText, setEditingText] = useState('')

  // Update nodes and edges when props change
  useEffect(() => {
    if (initialData?.jsonData) {
      const rawNodes = initialData.jsonData.nodes || []
      const edges = initialData.jsonData.edges || []
      
      // Validate and fix node types to ensure they are strings
      const validatedNodes = rawNodes.map(node => ({
        ...node,
        type: typeof node.type === 'string' ? node.type : 'default'
      }))
      
      
      setNodes(validatedNodes)
      setEdges(edges)
    }
  }, [initialData, setNodes, setEdges])

  // Call onSelectionChange when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange()
    }
  }, [nodes, edges, onSelectionChange])

  // Store onFlowchartChange in a ref to avoid dependency issues
  const onFlowchartChangeRef = useRef(onFlowchartChange)
  onFlowchartChangeRef.current = onFlowchartChange

  // Notify parent of changes
  useEffect(() => {
    if (onFlowchartChangeRef.current) {
      onFlowchartChangeRef.current({
        nodes,
        edges,
        viewport: reactFlowInstance?.getViewport() || { x: 0, y: 0, zoom: 1 }
      })
    }
  }, [nodes, edges, reactFlowInstance])

  const onConnect = useCallback(
    (params) => {
      const newEdge = addEdge({ ...params, type: currentEdgeType }, edges)
      setEdges(newEdge)
      if (onConnectProp) {
        onConnectProp(params)
      }
    },
    [edges, setEdges, onConnectProp, currentEdgeType]
  )

  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes)
      if (onNodesChangeProp) {
        onNodesChangeProp(changes)
      }
    },
    [onNodesChange, onNodesChangeProp]
  )

  const handleEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes)
      if (onEdgesChangeProp) {
        onEdgesChangeProp(changes)
      }
    },
    [onEdgesChange, onEdgesChangeProp]
  )

  // Add node method
  const addNode = useCallback((nodeType = 'default') => {
    // Ensure nodeType is a string
    const typeString = typeof nodeType === 'string' ? nodeType : 'default'
    
    const nodeLabels = {
      input: 'Start',
      default: 'Process',
      output: 'End',
      decision: 'Decision',
      process: 'Process',
      database: 'Database'
    }
    
    const newNode = {
      id: `node-${Date.now()}`,
      type: typeString,
      position: { 
        x: Math.random() * 300 + 50, 
        y: Math.random() * 200 + 50 
      },
      data: { label: nodeLabels[typeString] || 'New Node' }
    }
    
    setNodes(prev => [...prev, newNode])
  }, [setNodes])

  // Clear flowchart method
  const clearFlowchart = useCallback(() => {
    if (window.confirm('Are you sure you want to clear the flowchart?')) {
      setNodes([])
      setEdges([])
    }
  }, [setNodes, setEdges])

  // Fit view method
  const fitView = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView()
    }
  }, [reactFlowInstance])

  // Set edge type method
  const setEdgeType = useCallback((edgeType) => {
    setCurrentEdgeType(edgeType)
  }, [])

  // Auto layout method
  const autoLayout = useCallback((direction = 'vertical') => {
    if (!reactFlowInstance) return
    
    const currentNodes = reactFlowInstance.getNodes()
    const spacing = direction === 'vertical' ? { x: 0, y: 100 } : { x: 150, y: 0 }
    
    const layoutedNodes = currentNodes.map((node, index) => ({
      ...node,
      position: {
        x: direction === 'vertical' ? 200 : index * spacing.x + 50,
        y: direction === 'vertical' ? index * spacing.y + 50 : 200
      }
    }))
    
    setNodes(layoutedNodes)
  }, [reactFlowInstance, setNodes])

  // Edit selected node method
  const editSelectedNode = useCallback(() => {
    if (!reactFlowInstance) return false
    
    const selectedNodes = reactFlowInstance.getNodes().filter(node => node.selected)
    if (selectedNodes.length === 1) {
      const node = selectedNodes[0]
      setEditingItem('node')
      setEditingId(node.id)
      setEditingText(node.data.label || '')
      setIsTextModalOpen(true)
      return true
    }
    return false
  }, [reactFlowInstance])

  // Edit selected edge method
  const editSelectedEdge = useCallback(() => {
    if (!reactFlowInstance) return false
    
    const selectedEdges = reactFlowInstance.getEdges().filter(edge => edge.selected)
    if (selectedEdges.length === 1) {
      const edge = selectedEdges[0]
      setEditingItem('edge')
      setEditingId(edge.id)
      setEditingText(edge.label || '')
      setIsTextModalOpen(true)
      return true
    }
    return false
  }, [reactFlowInstance])

  // Handle text save from modal
  const handleTextSave = useCallback(async (newText) => {
    if (editingItem === 'node') {
      setNodes(prev => prev.map(n => 
        n.id === editingId 
          ? { ...n, data: { ...n.data, label: newText } }
          : n
      ))
    } else if (editingItem === 'edge') {
      setEdges(prev => prev.map(e => 
        e.id === editingId 
          ? { ...e, label: newText }
          : e
      ))
    }
    
    // Close modal
    setIsTextModalOpen(false)
    setEditingItem(null)
    setEditingId(null)
    setEditingText('')
  }, [editingItem, editingId, setNodes, setEdges])

  // Handle text modal close
  const handleTextModalClose = useCallback(() => {
    setIsTextModalOpen(false)
    setEditingItem(null)
    setEditingId(null)
    setEditingText('')
  }, [])

  // Use ref for stable note click handler
  const handleNoteClickRef = useRef((noteId) => {
    openTab({
      type: 'view',
      noteId: noteId,
      title: 'Loading...'
    })
  })

  // Update the ref when openTab changes
  useEffect(() => {
    handleNoteClickRef.current = (noteId) => {
      openTab({
        type: 'view',
        noteId: noteId,
        title: 'Loading...'
      })
    }
  }, [openTab])

  // Memoize node types to prevent recreation on every render
  const memoizedNodeTypes = useMemo(() => {
    return createNodeTypes(handleNoteClickRef.current)
  }, []) // Empty dependency array since we use ref

  // Undo/Redo methods (placeholder for now)
  const undoRedo = useCallback((action) => {
    console.log(`${action} action - not implemented yet`)
  }, [])

  // Delete selected nodes and edges
  const deleteSelected = useCallback(() => {
    if (!reactFlowInstance) return
    
    const currentNodes = reactFlowInstance.getNodes()
    const currentEdges = reactFlowInstance.getEdges()
    
    // Filter out selected nodes and edges
    const remainingNodes = currentNodes.filter(node => !node.selected)
    const remainingEdges = currentEdges.filter(edge => !edge.selected)
    
    setNodes(remainingNodes)
    setEdges(remainingEdges)
  }, [reactFlowInstance, setNodes, setEdges])

  // Check if any nodes are selected
  const hasSelectedNodes = useCallback(() => {
    if (!reactFlowInstance) return false
    return reactFlowInstance.getNodes().some(node => node.selected)
  }, [reactFlowInstance])

  // Check if any edges are selected
  const hasSelectedEdges = useCallback(() => {
    if (!reactFlowInstance) return false
    return reactFlowInstance.getEdges().some(edge => edge.selected)
  }, [reactFlowInstance])

  // Update nodes and edges from external data (for undo/redo)
  const updateFromData = useCallback((data) => {
    if (data.nodes) {
      setNodes(data.nodes)
    }
    if (data.edges) {
      setEdges(data.edges)
    }
  }, [setNodes, setEdges])

  // Export as image
  const exportAsImage = useCallback(async () => {
    if (!reactFlowWrapper.current) return null

    try {
      const dataUrl = await toPng(reactFlowWrapper.current, {
        backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
        width: 1200,
        height: 800,
      })
      
      return dataUrl
    } catch (error) {
      console.error('Failed to export flowchart as image:', error)
      return null
    }
  }, [theme])

  // Get flowchart data URL (for compatibility)
  const getFlowchartDataUrl = useCallback(async () => {
    return await exportAsImage()
  }, [exportAsImage])

  // Get current flowchart data
  const getFlowchartData = useCallback(() => {
    if (!reactFlowInstance) return { nodes: [], edges: [] }
    
    return {
      nodes: reactFlowInstance.getNodes(),
      edges: reactFlowInstance.getEdges(),
      viewport: reactFlowInstance.getViewport()
    }
  }, [reactFlowInstance])

  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance)
  }, [])

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    exportAsImage,
    getFlowchartData,
    getFlowchartDataUrl,
    addNode,
    clearFlowchart,
    fitView,
    setEdgeType,
    autoLayout,
    editSelectedNode,
    editSelectedEdge,
    undoRedo,
    deleteSelected,
    updateFromData,
    hasSelectedNodes,
    hasSelectedEdges
  }), [exportAsImage, getFlowchartData, getFlowchartDataUrl, addNode, clearFlowchart, fitView, setEdgeType, autoLayout, editSelectedNode, editSelectedEdge, undoRedo, deleteSelected, updateFromData, hasSelectedNodes, hasSelectedEdges])

  const backgroundVariant = 'dots'
  const backgroundClassName = theme === 'dark' ? 'bg-gray-900' : 'bg-white'

  return (
    <div 
      ref={reactFlowWrapper} 
      className={`w-full h-full ${backgroundClassName} ${className}`}
      style={{ width: '100%', height: '100%', minHeight: '500px', ...style }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        nodeTypes={memoizedNodeTypes}
        defaultEdgeOptions={{
          type: 'default',
          markerEnd: {
            type: 'arrowclosed',
            color: '#6b7280',
            width: 20,
            height: 20,
          },
        }}
        fitView
        attributionPosition="bottom-left"
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        panOnDrag={true}
        panOnScroll={true}
        panOnScrollMode="free"
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        selectNodesOnDrag={true}
        multiSelectionKeyCode="Meta"
        deleteKeyCode={["Delete", "Backspace"]}
        minZoom={0.1}
        maxZoom={4}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
      >
        <Controls />
        <MiniMap 
          nodeStrokeColor={(n) => {
            if (n.style?.background) return n.style.background;
            if (n.type === 'input') return '#10b981';
            if (n.type === 'default') return '#3b82f6';
            if (n.type === 'output') return '#ef4444';
            if (n.type === 'decision') return '#eab308';
            if (n.type === 'process') return '#3b82f6';
            if (n.type === 'database') return '#6b7280';
            return '#eee';
          }}
          nodeColor={(n) => {
            if (n.style?.background) return n.style.background;
            if (n.type === 'input') return '#10b981';
            if (n.type === 'default') return '#3b82f6';
            if (n.type === 'output') return '#ef4444';
            if (n.type === 'decision') return '#eab308';
            if (n.type === 'process') return '#3b82f6';
            if (n.type === 'database') return '#6b7280';
            return '#eee';
          }}
          style={{
            width: 100,
            height: 100,
          }}
        />
        <Background variant={backgroundVariant} gap={12} size={1} />
        
        {!readOnly && (
          <Panel position="top-right">
            <div className="bg-transparent p-2 rounded-lg">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                select node or edge to use the edit button
              </div>
            </div>
          </Panel>
        )}
      </ReactFlow>
      
      {/* Text Edit Modal */}
      <TextEditModal
        isOpen={isTextModalOpen}
        onClose={handleTextModalClose}
        onSave={handleTextSave}
        initialText={editingText}
        title={editingItem === 'node' ? 'Edit Node Text' : 'Edit Edge Label'}
        placeholder={editingItem === 'node' ? 'Enter node text...' : 'Enter edge label...'}
        multiline={editingItem === 'node'}
        maxLength={editingItem === 'node' ? 500 : 100}
        currentNoteId={noteId}
      />
    </div>
  )
})

FlowchartCanvas.displayName = 'FlowchartCanvas'

// Wrapper component with ReactFlowProvider
const FlowchartCanvasWrapper = forwardRef((props, ref) => (
  <ReactFlowProvider>
    <FlowchartCanvas {...props} ref={ref} />
  </ReactFlowProvider>
))

FlowchartCanvasWrapper.displayName = 'FlowchartCanvasWrapper'

export default FlowchartCanvasWrapper
